import { useCallback, useEffect, useRef } from 'react';
import { ChecklistPanel } from './ChecklistPanel';
import { ChecklistLauncher } from './ChecklistLauncher';
import { useOnboardingStore } from '../store/useOnboardingStore';
import { COPY, ONBOARDING_STEPS } from '../config/onboarding.config';
import type { ActiveCompany, CompanyProgress, StepKey } from '../config/onboarding.types';

interface OnboardingChecklistProps {
  company: ActiveCompany;
  /** Navega a la pantalla del paso. Recibe la ruta declarada en la config. */
  onNavigate: (route: string) => void;
  /** Abre el flujo de nueva venta. */
  onIssueInvoice: () => void;
  /** true mientras haya un modal o drawer abierto: el lanzador se retira. */
  isModalOpen?: boolean;
  /** Ruta donde no corresponde (emisión, POS). */
  isSuppressed?: boolean;
  /** Aviso tras ocultar, con dónde recuperarlo. */
  onHidden?: (message: string) => void;
}

/** Espera antes de la apertura de bienvenida, para no abrir de golpe al cargar. */
const WELCOME_DELAY_MS = 480;
const POPOVER_ID = 'checklist-inicio';

/**
 * Orquesta el checklist: un lanzador fijo abajo a la derecha y el popover que
 * se abre sobre él. Nunca ocupa la zona de trabajo: el lanzador es una píldora
 * en la esquina y el popover solo aparece cuando el usuario lo pide (o una
 * única vez como bienvenida).
 *
 * Reglas de visibilidad:
 * - Retirado tras la primera emisión, oculto por el usuario, o sin datos: nada.
 * - Por defecto solo en la primera empresa y mientras haya avance reciente
 *   (STALE_AFTER_DAYS). Desde Ayuda (store.restore) se fuerza igual.
 * - Con un modal abierto o en rutas de emisión: nada en pantalla.
 *
 * La celebración de "primera venta" NO se dispara desde aquí: se decide en
 * el momento real de éxito de la emisión (useOnboardingStore.notifyFirstSale).
 */
export function OnboardingChecklist({
  company,
  onNavigate,
  onIssueInvoice,
  isModalOpen = false,
  isSuppressed = false,
  onHidden,
}: OnboardingChecklistProps) {
  const store = useOnboardingStore();
  const progress = store.progress[company.id];
  const ui = store.getUi(company.id);
  const launcherRef = useRef<HTMLButtonElement>(null);

  const isRetired = store.isRetired(company.id);
  const isStale = store.isStale(company.id);
  const isEligible = ui.forcedVisible || (company.isFirstCompany && !isStale);
  const isActive = !!progress && !isRetired && !ui.isHidden && isEligible;
  const isVisible = isActive && !isModalOpen && !isSuppressed;

  // Bienvenida: el popover se abre solo una vez, y nunca encima de otro modal
  // (isVisible ya es false mientras haya uno abierto).
  useEffect(() => {
    if (!isVisible || ui.welcomeShown) return;
    const t = window.setTimeout(
      () => useOnboardingStore.getState().markWelcomeShown(company.id),
      WELCOME_DELAY_MS,
    );
    return () => window.clearTimeout(t);
  }, [isVisible, ui.welcomeShown, company.id]);

  const close = useCallback(() => {
    useOnboardingStore.getState().closePanel(company.id);
    launcherRef.current?.focus();
  }, [company.id]);

  useEffect(() => {
    if (!isVisible || !ui.isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isVisible, ui.isOpen, close]);

  const handleGoToStep = useCallback(
    (key: StepKey) => {
      const step = ONBOARDING_STEPS.find((s) => s.key === key);
      if (!step) return;
      useOnboardingStore.getState().closePanel(company.id);
      onNavigate(step.route);
    },
    [company.id, onNavigate],
  );

  const handleIssue = useCallback(() => {
    useOnboardingStore.getState().closePanel(company.id);
    onIssueInvoice();
  }, [company.id, onIssueInvoice]);

  if (!isVisible || !progress) return null;

  const completed = store.completedCount(company.id);
  const isReady = store.isReadyToIssue(company.id);

  return (
    <div className="pointer-events-none fixed bottom-[calc(20px+env(safe-area-inset-bottom))] right-5 z-[35] flex flex-col items-end gap-3 max-sm:bottom-[calc(12px+env(safe-area-inset-bottom))] max-sm:right-3">
      {ui.isOpen && (
        <ChecklistPanel
          id={POPOVER_ID}
          className="pointer-events-auto"
          company={company}
          progress={progress}
          completed={completed}
          remaining={store.remaining(company.id)}
          isReady={isReady}
          openStepKey={ui.openStepKey ?? firstPending(progress)}
          onClose={close}
          onHide={() => {
            store.hide(company.id);
            onHidden?.(COPY.hiddenNotice);
          }}
          onToggleStep={(k) => store.toggleStep(company.id, k)}
          onGoToStep={handleGoToStep}
          onIssue={handleIssue}
        />
      )}
      <ChecklistLauncher
        ref={launcherRef}
        completed={completed}
        nextStep={store.nextStep(company.id)}
        isReady={isReady}
        isOpen={ui.isOpen}
        hasAlert={ui.hasAlert && !ui.isOpen}
        controlsId={POPOVER_ID}
        onToggle={() => (ui.isOpen ? close() : store.openPanel(company.id))}
      />
    </div>
  );
}

/** Paso sugerido: el primero pendiente. No bloquea a los demás. */
function firstPending(progress: CompanyProgress): StepKey | null {
  const next = ONBOARDING_STEPS.find((s) => !progress[s.key]);
  return next?.key ?? null;
}
